import axiosInstance from "utils/axios"

/**
 * WhatsApp Template Service
 * Handles all WhatsApp template API interactions
 */

// Upload media files to WhatsApp
export const uploadWhatsAppMedia = async (files) => {
  try {
    const formData = new FormData()

    // Add all files to FormData
    files.forEach((file) => {
      // Get the actual file object (could be cropped, trimmed, or original)
      const actualFile = file.croppedFile || file.trimmedFile || file.file || file

      // Get the original file for fallback filename
      const originalFile = file.originalFile || file.file

      // Determine the proper filename
      // Priority: file.name > actualFile.name > originalFile.name > generated name
      let fileName = file.name || actualFile?.name || originalFile?.name

      // If still no filename, generate one based on type
      if (!fileName || fileName === 'blob') {
        const mimeType = actualFile?.type || file.type || 'application/octet-stream'
        const extension = mimeType.split('/')[1]?.split(';')[0] || 'bin'
        fileName = `media_${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`
      }

      // Always create a proper File object to ensure filename is set correctly
      // This handles both Blob and File objects uniformly
      if (actualFile instanceof Blob) {
        const fileWithName = new File([actualFile], fileName, {
          type: actualFile.type || file.type || 'application/octet-stream'
        })
        formData.append('files', fileWithName, fileName)
      } else {
        // Fallback for non-Blob objects (shouldn't happen normally)
        formData.append('files', actualFile, fileName)
      }
    })

    const response = await axiosInstance.post("/api/MetaWhatsApp/upload-media", formData, {
      // Content-Type handled by axios interceptor for FormData
      timeout: 120000, // 2 minute timeout for media uploads
    })

    // Check for new API response format with data array inside response
    if (response.data?.statusCode === 200 && Array.isArray(response.data.data)) {
      return {
        success: true,
        data: response.data.data, // Array of { mediaId, mediaType, caption }
        message: response.data.message || "Media uploaded successfully",
      }
    }
    // Fallback: API returns array directly (old format)
    else if (Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data, // Array of { mediaId, mediaType }
        message: "Media uploaded successfully",
      }
    } else {
      throw new Error("Invalid response format from upload API")
    }
  } catch (error) {
    console.error("Error uploading WhatsApp media:", error)

    let errorMessage = "Failed to upload media"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Send WhatsApp media to leads
export const sendWhatsAppMedia = async (leads, whatsappMedias) => {
  try {
    const requestBody = {
      leads,
      whatsappMedias
    }

    const response = await axiosInstance.post("/api/MetaWhatsApp/send-media", requestBody)

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "WhatsApp media sent successfully",
      }
    } else {
      throw new Error(response.data.message || "Failed to send WhatsApp media")
    }
  } catch (error) {
    console.error("Error sending WhatsApp media:", error)

    let errorMessage = "Failed to send WhatsApp media"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Transform template data to match API format
const transformToApiFormat = (templateData) => {
  const {
    name,
    language,
    category,
    headerText,
    bodyText,
    footerText,
    buttons = [],
    headerExamples = [],
    bodyExamples = [],
    mediaUrl,
    ...rest
  } = templateData

  // Components array removed - not needed for API request
  // The backend will construct components from individual fields

  // Convert category string to number if needed
  const getCategoryId = (cat) => {
    switch (cat) {
      case "MARKETING": return 0
      case "UTILITY": return 1
      case "AUTHENTICATION": return 2
      default: return 0
    }
  }

  return {
    // templateTypeId removed - not needed in request body
    channelId: 3, // WhatsApp channel ID
    name,
    language,
    category: getCategoryId(category),
    headerText: headerText || "",
    bodyText: bodyText || "",
    footerText: footerText || "",
    buttons: buttons.map(btn => ({
      type: btn.type,
      text: btn.text,
      url: btn.url || "",
      phone_number: btn.phoneNumber || "",
      example: btn.example || []
    })),
    // components field removed - not needed in request body
    headerExamples,
    bodyExamples,
    ...rest
  }
}

// Create a new WhatsApp template
export const createWhatsAppTemplate = async (templateData) => {
  try {
    const apiData = transformToApiFormat(templateData)
    const response = await axiosInstance.post("/api/WhatsAppTemplate", apiData)

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to create WhatsApp template")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template created successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template created successfully",
    }
  } catch (error) {
    console.error("Error creating WhatsApp template:", error)

    let errorMessage = "Failed to create WhatsApp template"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Update an existing WhatsApp template
export const updateWhatsAppTemplate = async (templateData) => {
  try {
    const apiData = transformToApiFormat(templateData)
    const response = await axiosInstance.put(`/api/WhatsAppTemplate/${templateData.templateId}`, apiData)

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to update WhatsApp template")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template updated successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template updated successfully",
    }
  } catch (error) {
    console.error("Error updating WhatsApp template:", error)

    let errorMessage = "Failed to update WhatsApp template"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Get template by ID
export const getWhatsAppTemplate = async (templateId) => {
  try {
    const response = await axiosInstance.get(`/api/WhatsAppTemplate/${templateId}`)

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to fetch WhatsApp template")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template fetched successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template fetched successfully",
    }
  } catch (error) {
    console.error("Error fetching WhatsApp template:", error)

    let errorMessage = "Failed to fetch WhatsApp template"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Get all templates for company
export const getCompanyWhatsAppTemplates = async (companyId, params = {}) => {
  try {
    const response = await axiosInstance.get(`/api/WhatsAppTemplate/company/${companyId}`, {
      params
    })

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to fetch company WhatsApp templates")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Templates fetched successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Templates fetched successfully",
    }
  } catch (error) {
    console.error("Error fetching company WhatsApp templates:", error)

    let errorMessage = "Failed to fetch company WhatsApp templates"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Get WhatsApp templates with pagination and filters
export const getWhatsAppTemplates = async ({ isActive = true, pageNumber = 1, pageSize = 50, search = "" } = {}) => {
  try {
    const requestBody = {
      isActive,
      pageNumber,
      pageSize
    }


    const response = await axiosInstance.post("/api/WhatsAppTemplate/GetTemplates", requestBody)


    // Parse the data field if it's a JSON string
    let parsedData = []
    if (response.data && response.data.data) {
      if (typeof response.data.data === 'string') {
        try {
          parsedData = JSON.parse(response.data.data)
        } catch (parseError) {
          console.error("Error parsing template data:", parseError)
          parsedData = []
        }
      } else if (Array.isArray(response.data.data)) {
        parsedData = response.data.data
      }
    }


    // Filter by search term if provided (client-side filtering)
    let filteredData = Array.isArray(parsedData) ? parsedData : []
    if (search && search.trim()) {
      const searchLower = search.toLowerCase()
      filteredData = filteredData.filter(template =>
        (template.Name && template.Name.toLowerCase().includes(searchLower)) ||
        (template.BodyText && template.BodyText.toLowerCase().includes(searchLower))
      )
    }

    return {
      success: true,
      data: filteredData,
      total: response.data.totalRecords || filteredData.length,
      message: response.data.message || "Templates fetched successfully"
    }
  } catch (error) {
    console.error("Error fetching WhatsApp templates:", error)
    return {
      success: false,
      data: [],
      total: 0,
      error: error.response?.data?.message || error.message || "Failed to fetch WhatsApp templates"
    }
  }
}

// Get approved WhatsApp templates for messaging
export const getApprovedWhatsAppTemplatesForMessaging = async ({ isActive = true, pageNumber = 1, pageSize = 50, search = "" } = {}) => {
  try {
    const requestBody = {
      isActive,
      pageNumber,
      pageSize,
      search // Ensure search is passed if expected by backend, or applied client-side if needed
    }

    const response = await axiosInstance.post("/api/WhatsAppTemplate/GetApprovedTemplatesForMessaging", requestBody, {
      params: {
        pageNumber,
        pageSize
      }
    })

    // Parse the data field if it's a JSON string
    let parsedData = []
    if (response.data && response.data.data) {
      if (typeof response.data.data === 'string') {
        try {
          parsedData = JSON.parse(response.data.data)
        } catch (parseError) {
          console.error("Error parsing template data:", parseError)
          parsedData = []
        }
      } else if (Array.isArray(response.data.data)) {
        parsedData = response.data.data
      }
    }

    // Filter by search term if provided (client-side filtering as fallback or additional filter)
    let filteredData = Array.isArray(parsedData) ? parsedData : []
    if (search && search.trim()) {
      const searchLower = search.toLowerCase()
      filteredData = filteredData.filter(template =>
        (template.Name && template.Name.toLowerCase().includes(searchLower)) ||
        (template.BodyText && template.BodyText.toLowerCase().includes(searchLower))
      )
    }

    return {
      success: true,
      data: filteredData,
      total: response.data.totalRecords || filteredData.length,
      message: response.data.message || "Templates fetched successfully"
    }
  } catch (error) {
    console.error("Error fetching approved WhatsApp templates:", error)
    return {
      success: false,
      data: [],
      total: 0,
      error: error.response?.data?.message || error.message || "Failed to fetch approved WhatsApp templates"
    }
  }
}

// Submit template to Meta for approval
export const submitTemplateToMeta = async (templateId, allowCategoryChange = true) => {
  try {
    const response = await axiosInstance.post("/api/WhatsAppTemplate/SubmitToMeta", {
      templateId,
      allowCategoryChange
    })

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to submit template to Meta")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template submitted to Meta successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template submitted to Meta successfully",
    }
  } catch (error) {
    console.error("Error submitting template to Meta:", error)

    let errorMessage = "Failed to submit template to Meta"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Sync templates from Meta
export const syncTemplatesFromMeta = async () => {
  try {
    const response = await axiosInstance.post("/api/WhatsAppTemplate/sync")

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to sync templates from Meta")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Templates synced from Meta successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Templates synced from Meta successfully",
    }
  } catch (error) {
    console.error("Error syncing templates from Meta:", error)

    let errorMessage = "Failed to sync templates from Meta"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Sync templates from Meta (New Endpoint)
export const syncWhatsAppTemplatesFromMeta = async () => {
  try {
    // Post with empty body as requested
    const response = await axiosInstance.post("/api/WhatsAppTemplate/SyncFromMeta", {})

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to sync templates from Meta")
    }

    // Check for success status
    if (responseData?.statusCode === 200 || responseData?.success) {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Templates synced from Meta successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Templates synced from Meta successfully",
    }
  } catch (error) {
    console.error("Error syncing templates from Meta (New):", error)

    let errorMessage = "Failed to sync templates from Meta"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Delete template
export const deleteWhatsAppTemplate = async (templateId) => {
  try {
    const response = await axiosInstance.delete(`/api/WhatsAppTemplate/${templateId}`)

    const responseData = response.data

    // Check if the response indicates failure
    if (responseData?.status === "Failure" || responseData?.statusCode >= 400) {
      throw new Error(responseData?.message || "Failed to delete WhatsApp template")
    }

    // Check for success status
    if (responseData?.statusCode === 200 && responseData?.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Template deleted successfully",
      }
    }

    // If we get here, return the response but with success flag
    return {
      success: true,
      data: responseData.data,
      message: responseData.message || "Template deleted successfully",
    }
  } catch (error) {
    console.error("Error deleting WhatsApp template:", error)

    let errorMessage = "Failed to delete WhatsApp template"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Send WhatsApp message to a single lead
export const sendWhatsAppMessage = async (leadId, phoneNumber, message) => {
  try {
    const requestBody = {
      leads: {
        [leadId]: phoneNumber
      },
      message
    }


    const response = await axiosInstance.post("/api/MetaWhatsApp/send-text", requestBody)

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "WhatsApp message sent successfully",
      }
    } else {
      throw new Error(response.data.message || "Failed to send WhatsApp message")
    }
  } catch (error) {
    console.error("Error sending WhatsApp message:", error)

    let errorMessage = "Failed to send WhatsApp message"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Send WhatsApp message to multiple leads
export const sendWhatsAppMessageToMultiple = async (leads, message) => {
  try {
    // Build the leads object with leadId: phoneNumber mapping
    const leadsMap = {}
    leads.forEach(lead => {
      const leadId = lead.id || lead.leadId || lead.LeadId || lead.userId
      const phoneNumber = lead.mobile || lead.Mobile || lead.phone || lead.PhoneNumber || lead.number

      if (leadId && phoneNumber && phoneNumber !== "N/A" && phoneNumber.trim() !== "") {
        leadsMap[leadId] = phoneNumber
      }
    })

    if (Object.keys(leadsMap).length === 0) {
      throw new Error("No valid leads with phone numbers found")
    }

    const requestBody = {
      leads: leadsMap,
      message
    }

    const response = await axiosInstance.post("/api/MetaWhatsApp/send-text", requestBody)

    if (
      response.data.statusCode === 200 &&
      (response.data.status === "Success" || response.data.status === "success")
    ) {
      return {
        success: true,
        successCount: Object.keys(leadsMap).length,
        failureCount: 0,
        totalCount: Object.keys(leadsMap).length,
        data: response.data.data,
        message: response.data.message || `WhatsApp messages sent to ${Object.keys(leadsMap).length} leads successfully`,
      }
    } else {
      throw new Error(response.data.message || "Failed to send WhatsApp messages")
    }
  } catch (error) {
    console.error("Error sending bulk WhatsApp messages:", error)

    let errorMessage = "Failed to send WhatsApp messages"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Utility functions for WhatsApp templates

// Extract numbered variables from text ({{1}}, {{2}}, etc.)
export const extractNumberedVariables = (text) => {
  if (!text) return []
  const matches = text.match(/\{\{\d+\}\}/g) || []
  return [...new Set(matches)].sort()
}

// Extract named variables from text ({{FullName}}, {{Email}}, etc.)
export const extractNamedVariables = (text) => {
  if (!text) return []
  // Match {{word}} but NOT {{number}}
  const matches = text.match(/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g) || []
  return [...new Set(matches)].sort()
}

// Get all variables from template components
export const getAllTemplateVariables = (headerText, bodyText, footerText, buttons = []) => {
  const allVariables = new Set()

  // Extract from header
  extractNumberedVariables(headerText).forEach(v => allVariables.add(v))

  // Extract from body
  extractNumberedVariables(bodyText).forEach(v => allVariables.add(v))

  // Footer doesn't support variables - commented out
  // Extract from footer
  // extractNumberedVariables(footerText).forEach(v => allVariables.add(v))

  // Extract from buttons
  buttons.forEach(button => {
    if (button.url) {
      extractNumberedVariables(button.url).forEach(v => allVariables.add(v))
    }
    if (button.phoneNumber) {
      extractNumberedVariables(button.phoneNumber).forEach(v => allVariables.add(v))
    }
    if (button.text) {
      extractNumberedVariables(button.text).forEach(v => allVariables.add(v))
    }
  })

  return Array.from(allVariables).sort()
}

// Replace numbered variables with example values
export const replaceNumberedVariables = (text, examples = []) => {
  if (!text) return text

  let processedText = text
  const variables = extractNumberedVariables(text)

  variables.forEach((variable, index) => {
    const variableNumber = variable.match(/\d+/)[0]
    const exampleValue = examples[parseInt(variableNumber) - 1] || `Example${variableNumber}`
    processedText = processedText.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), exampleValue)
  })

  return processedText
}

// Validate template data before submission
export const validateTemplateData = (templateData) => {
  const errors = {}

  if (!templateData.name?.trim()) {
    errors.name = "Template name is required"
  } else if (!/^[a-z0-9_]+$/.test(templateData.name)) {
    errors.name = "Template name must be lowercase with underscores only"
  }

  if (!templateData.bodyText?.trim()) {
    errors.bodyText = "Body text is required"
  }

  if (!templateData.language) {
    errors.language = "Language is required"
  }

  if (!templateData.category) {
    errors.category = "Category is required"
  }

  // Validate header variables
  if (templateData.headerText) {
    const headerNumberedVars = extractNumberedVariables(templateData.headerText)
    const headerNamedVars = extractNamedVariables(templateData.headerText)

    // Check if mixing numbered and named variables
    if (headerNumberedVars.length > 0 && headerNamedVars.length > 0) {
      errors.headerText = "Header cannot mix numbered ({{1}}) and named ({{FullName}}) variables. Use only one type."
    } else if (headerNumberedVars.length > 1 || headerNamedVars.length > 1) {
      errors.headerText = "Header can only contain 1 variable"
    }
  }

  // Validate body variables - cannot mix numbered and named
  if (templateData.bodyText) {
    const bodyNumberedVars = extractNumberedVariables(templateData.bodyText)
    const bodyNamedVars = extractNamedVariables(templateData.bodyText)

    if (bodyNumberedVars.length > 0 && bodyNamedVars.length > 0) {
      errors.bodyText = "Body cannot mix numbered ({{1}}, {{2}}) and named ({{FullName}}, {{Email}}) variables. Use only one type."
    }
  }

  // Footer doesn't support variables - validation commented out
  // Validate footer variables - cannot mix numbered and named
  // if (templateData.footerText) {
  //   const footerNumberedVars = extractNumberedVariables(templateData.footerText)
  //   const footerNamedVars = extractNamedVariables(templateData.footerText)
  //   
  //   if (footerNumberedVars.length > 0 && footerNamedVars.length > 0) {
  //     errors.footerText = "Footer cannot mix numbered ({{1}}, {{2}}) and named ({{FullName}}, {{Email}}) variables. Use only one type."
  //   }
  // }

  // Validate buttons
  if (templateData.buttons && templateData.buttons.length > 3) {
    errors.buttons = "Maximum 3 buttons allowed"
  }

  templateData.buttons?.forEach((button, index) => {
    if (!button.text?.trim()) {
      errors[`button${index}Text`] = "Button text is required"
    }

    if (button.type === "URL" && !button.url?.trim()) {
      errors[`button${index}Url`] = "URL is required for URL buttons"
    }

    if (button.type === "PHONE_NUMBER" && !button.phoneNumber?.trim()) {
      errors[`button${index}Phone`] = "Phone number is required for phone buttons"
    }
  })

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}

// Generate example arrays for API submission
// Each section (header, body, footer) has independent variable numbering
// Handles both numbered variables ({{1}}, {{2}}) and named variables ({{FullName}}, {{Email}})
export const generateExampleArrays = (headerText, bodyText, footerText, exampleValues = {}) => {
  // Extract both numbered and named variables
  const headerNumberedVars = extractNumberedVariables(headerText)
  const headerNamedVars = extractNamedVariables(headerText)
  const bodyNumberedVars = extractNumberedVariables(bodyText)
  const bodyNamedVars = extractNamedVariables(bodyText)
  // Footer doesn't support variables - commented out
  // const footerNumberedVars = extractNumberedVariables(footerText)
  // const footerNamedVars = extractNamedVariables(footerText)

  // Helper function to get example value for a variable with section context
  const getExampleValue = (variable, section, fallback) => {
    const varKey = variable.replace(/[{}]/g, '')
    // Check if it's a numbered variable
    if (/^\d+$/.test(varKey)) {
      // Use section-specific key (e.g., header_1, body_2, footer_1)
      const sectionKey = `${section}_${varKey}`
      return exampleValues[sectionKey] || exampleValues[varKey] || fallback
    }
    // Named variable (same across all sections)
    return exampleValues[varKey] || fallback
  }

  // Header examples (combines numbered and named variables)
  const headerExamples = []
  if (headerNumberedVars.length > 0 || headerNamedVars.length > 0) {
    // Numbered variables first
    headerNumberedVars.forEach((v, index) => {
      headerExamples.push(getExampleValue(v, 'header', `Example${index + 1}`))
    })
    // Then named variables
    headerNamedVars.forEach(v => {
      const varKey = v.replace(/[{}]/g, '')
      headerExamples.push(getExampleValue(v, 'header', varKey))
    })
  }

  // Body examples (combines numbered and named variables) - wrapped in array
  let bodyExamples = []
  if (bodyNumberedVars.length > 0 || bodyNamedVars.length > 0) {
    const bodyExampleArray = []
    // Numbered variables first
    bodyNumberedVars.forEach((v, index) => {
      bodyExampleArray.push(getExampleValue(v, 'body', `Example${index + 1}`))
    })
    // Then named variables
    bodyNamedVars.forEach(v => {
      const varKey = v.replace(/[{}]/g, '')
      bodyExampleArray.push(getExampleValue(v, 'body', varKey))
    })
    bodyExamples = [bodyExampleArray]
  }

  // Footer doesn't support variables - return empty array
  // Footer examples (combines numbered and named variables) - wrapped in array like body
  let footerExamples = []
  // if (footerNumberedVars.length > 0 || footerNamedVars.length > 0) {
  //   const footerExampleArray = []
  //   // Numbered variables first
  //   footerNumberedVars.forEach((v, index) => {
  //     footerExampleArray.push(getExampleValue(v, 'footer', `Example${index + 1}`))
  //   })
  //   // Then named variables
  //   footerNamedVars.forEach(v => {
  //     const varKey = v.replace(/[{}]/g, '')
  //     footerExampleArray.push(getExampleValue(v, 'footer', varKey))
  //   })
  //   footerExamples = [footerExampleArray]
  // }

  return { headerExamples, bodyExamples, footerExamples }
}

// Send WhatsApp template message using Meta API
export const sendWhatsAppTemplateMessage = async (leadId, phoneNumber, templateName, variables) => {
  try {
    // Build parameters array in order: header -> body -> footer
    const parameters = []

    // Helper to sort variables by number
    const sortByNumber = (entries) => {
      return entries.sort(([aKey], [bKey]) => {
        const aNum = parseInt(aKey) || 0
        const bNum = parseInt(bKey) || 0
        return aNum - bNum
      })
    }

    // Add header variables (if any) - sorted by number
    if (variables.header) {
      const sortedHeader = sortByNumber(Object.entries(variables.header))
      sortedHeader.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    // Add body variables (if any) - sorted by number
    if (variables.body) {
      const sortedBody = sortByNumber(Object.entries(variables.body))
      sortedBody.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    // Add footer variables (if any) - sorted by number
    if (variables.footer) {
      const sortedFooter = sortByNumber(Object.entries(variables.footer))
      sortedFooter.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    const requestBody = {
      leads: {
        [leadId]: phoneNumber
      },
      templateName,
      parameters
    }


    const response = await axiosInstance.post("/api/MetaWhatsApp/send-template", requestBody)


    if (response.data.statusCode === 200) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "WhatsApp template message sent successfully",
      }
    } else {
      throw new Error(response.data.message || "Failed to send WhatsApp template message")
    }
  } catch (error) {
    console.error("Error sending WhatsApp template message:", error)

    let errorMessage = "Failed to send WhatsApp template message"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}

// Send WhatsApp template message to multiple leads
export const sendWhatsAppTemplateMessageToMultiple = async (leads, templateName, variables) => {
  try {
    // Build the leads object with leadId: phoneNumber mapping
    const leadsMap = {}
    leads.forEach(lead => {
      const leadId = lead.id || lead.leadId || lead.LeadId || lead.userId
      const phoneNumber = lead.mobile || lead.Mobile || lead.phone || lead.PhoneNumber || lead.number

      if (leadId && phoneNumber && phoneNumber !== "N/A" && phoneNumber.trim() !== "") {
        leadsMap[leadId] = phoneNumber
      }
    })

    if (Object.keys(leadsMap).length === 0) {
      throw new Error("No valid leads with phone numbers found")
    }

    // Build parameters array in order: header -> body -> footer
    const parameters = []

    // Helper to sort variables by number
    const sortByNumber = (entries) => {
      return entries.sort(([aKey], [bKey]) => {
        const aNum = parseInt(aKey) || 0
        const bNum = parseInt(bKey) || 0
        return aNum - bNum
      })
    }

    // Add header variables (if any) - sorted by number
    if (variables.header) {
      const sortedHeader = sortByNumber(Object.entries(variables.header))
      sortedHeader.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    // Add body variables (if any) - sorted by number
    if (variables.body) {
      const sortedBody = sortByNumber(Object.entries(variables.body))
      sortedBody.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    // Add footer variables (if any) - sorted by number
    if (variables.footer) {
      const sortedFooter = sortByNumber(Object.entries(variables.footer))
      sortedFooter.forEach(([varName, value]) => {
        if (value && value.trim()) {
          parameters.push({
            type: "text",
            text: value
          })
        }
      })
    }

    const requestBody = {
      leads: leadsMap,
      templateName,
      parameters
    }


    const response = await axiosInstance.post("/api/MetaWhatsApp/send-template", requestBody)

    if (response.data.statusCode === 200) {
      return {
        success: true,
        successCount: Object.keys(leadsMap).length,
        failureCount: 0,
        totalCount: Object.keys(leadsMap).length,
        data: response.data.data,
        message: response.data.message || `WhatsApp template messages sent to ${Object.keys(leadsMap).length} leads successfully`,
      }
    } else {
      throw new Error(response.data.message || "Failed to send WhatsApp template messages")
    }
  } catch (error) {
    console.error("Error sending bulk WhatsApp template messages:", error)

    let errorMessage = "Failed to send WhatsApp template messages"
    if (error.response) {
      const status = error.response.status
      const serverMessage = error.response.data?.message || error.response.data?.error
      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server"
    } else {
      errorMessage = error.message || "Unknown error occurred"
    }

    throw new Error(errorMessage)
  }
}